<script setup>
import { router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import { useCurrency } from '@/composables/useCurrency.js'

defineOptions({ layout: AppLayout })
defineProps({ deliveries: { type: Array, default: () => [] } })

const { t } = useI18n()
const { formatMoney, formatDate } = useCurrency()
</script>

<template>
  <PageShell :title="t('deliveries.title')">
    <table class="del-table">
      <thead>
        <tr>
          <th>{{ t('deliveries.columns.kind') }}</th>
          <th>{{ t('deliveries.columns.customer') }}</th>
          <th>{{ t('deliveries.columns.driver') }}</th>
          <th>{{ t('deliveries.columns.address') }}</th>
          <th>{{ t('deliveries.columns.scheduled') }}</th>
          <th>{{ t('deliveries.columns.status') }}</th>
          <th style="text-align: right;">{{ t('deliveries.index.fee') }}</th>
          <th>{{ t('deliveries.index.paid') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="d in deliveries"
          :key="d.id"
          class="del-row"
          @click="router.visit(`/rentals/${d.rental_id}`)"
        >
          <td>{{ d.kind === 'handover' ? t('deliveries.kind.handover') : t('deliveries.kind.pickup') }}</td>
          <td>{{ d.customer_name }}</td>
          <td>{{ d.driver_name || '—' }}</td>
          <td>{{ d.address || '—' }}</td>
          <td>{{ d.scheduled_for || '—' }}</td>
          <td>
            <span :class="['pill', `pill--${d.status}`]">{{ d.status }}</span>
          </td>
          <td class="tabular-nums fw-value" style="text-align: right;">{{ formatMoney(d.fee_cents) }}</td>
          <td>
            <span
              v-if="d.paid_at"
              class="pill pill--ok"
            >{{ formatDate(d.paid_at) }}</span>
            <span
              v-else
              class="text-xs"
              style="color: var(--muted);"
            >{{ t('deliveries.index.unpaid') }}</span>
          </td>
        </tr>
        <tr v-if="deliveries.length === 0">
          <td
            colspan="8"
            class="empty"
          >
            {{ t('deliveries.empty') }}
          </td>
        </tr>
      </tbody>
    </table>
  </PageShell>
</template>

<style scoped>
.del-table { width: 100%; border-collapse: collapse; }
.del-table th,
.del-table td { padding: 0.6rem 0.5rem; text-align: left; border-bottom: 1px solid var(--line); }
.del-table th { font-size: 0.75rem; text-transform: uppercase; color: var(--muted); font-weight: 600; letter-spacing: 0.04em; }
.del-row { cursor: pointer; transition: background 120ms; }
.del-row:hover { background: var(--surfaceAlt); }
.pill { display: inline-block; padding: 0.15rem 0.5rem; border-radius: 999px; font-size: 0.75rem; }
.pill--pending     { background: color-mix(in oklch, var(--warn) 14%, var(--surface));    color: var(--warn); }
.pill--assigned    { background: color-mix(in oklch, var(--accent) 14%, var(--surface));  color: var(--accent); }
.pill--in_progress { background: color-mix(in oklch, var(--accent) 22%, var(--surface));  color: var(--accent); }
.pill--ok          { background: color-mix(in oklch, var(--ok) 14%, var(--surface));       color: var(--ok); }
.empty { text-align: center; color: var(--muted); padding: 2rem; }
</style>
